import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Package, Grid3x3, ShoppingCart, Plus, Pencil, Trash2, X, Camera,
  DollarSign, Clock, Mail, CheckCircle2, Loader2, MessageSquare, Download, CalendarOff,
  BarChart3, Save, CalendarDays, ChevronLeft, ChevronRight, Upload, ImagePlus,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import {
  adminStats, fetchProducts, fetchCategories,
  adminCreateProduct, adminUpdateProduct, adminDeleteProduct,
  adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
  adminOrders, adminUpdateOrder, adminMessages, adminDeleteOrder,
  adminBlockedDates, adminAddBlockedDate, adminDeleteBlockedDate,
  adminGetSettings, adminUpdateSettings, adminSalesReport, uploadImage,
  adminEmailHealth, adminSendTestEmail, adminSaveEmailConfig,
} from "../../lib/api";
import { LOGO_URL } from "../../lib/brand";
import * as XLSX from "xlsx";

const EMPTY_PRODUCT = {
  name: "", price: 0, image: "", gallery: [], description: "", category: "",
  subcollection: "", featured: false, inStock: true, stock: 0, type: "product",
  tagline: "", variants: [], sizes: [], deliverables: [], location: "", turnaround: "",
};

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls = "w-full bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0e2a1e]/40";

const ImageUploader = ({ onUploaded, multiple = false, label = "Upload image" }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const { toast } = useToast();
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const url = await uploadImage(f);
        onUploaded(url);
      }
      toast({ title: files.length > 1 ? `${files.length} images uploaded` : "Image uploaded" });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "Try a JPG/PNG under 10MB", variant: "destructive" });
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple={multiple} hidden onChange={handle} data-testid="image-upload-input" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="image-upload-btn"
        className="flex items-center gap-2 bg-[#0e2a1e] text-[#f4f2ea] text-xs font-semibold px-3 py-2 rounded-md hover:bg-[#153a2a] disabled:opacity-60 transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "Uploading…" : label}
      </button>
    </>
  );
};


const ProductForm = ({ initial, categories, onSave, onClose }) => {
  const [p, setP] = useState({ ...EMPTY_PRODUCT, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await onSave({
        ...p,
        price: parseFloat(p.price) || 0,
        stock: parseInt(p.stock, 10) || 0,
        variants: (p.variants || []).map((v) => ({ name: v.name, price: parseFloat(v.price) || 0 })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-center overflow-y-auto py-10 px-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">
            {initial?.id ? "Edit" : "New"} {p.type === "service" ? "service" : "product"}
          </h3>
          <button onClick={onClose} className="text-[#0e2a1e]/50 hover:text-[#0e2a1e]"><X size={22} /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Type">
            <select value={p.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
              <option value="product">Product</option>
              <option value="service">Photography Service</option>
            </select>
          </Field>
          <Field label="Category">
            <select value={p.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Name"><input className={inputCls} value={p.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Base price ($)"><input type="number" step="0.01" className={inputCls} value={p.price} onChange={(e) => set("price", e.target.value)} /></Field>
          <Field label="Stock quantity"><input type="number" step="1" min="0" className={inputCls} value={p.stock} onChange={(e) => set("stock", e.target.value)} /></Field>
          <div className="col-span-2">
            <Field label="Main image">
              <div className="flex items-center gap-3">
                {p.image ? (
                  <img src={p.image} alt="" className="w-14 h-14 rounded-md object-cover bg-[#f4f2ea] border border-[#0e2a1e]/10" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-[#f4f2ea] border border-dashed border-[#0e2a1e]/20 flex items-center justify-center text-[#0e2a1e]/30"><ImagePlus size={18} /></div>
                )}
                <div className="flex-1 space-y-1.5">
                  <ImageUploader onUploaded={(url) => set("image", url)} label="Upload image" />
                  <input className={inputCls} placeholder="…or paste an image URL" value={p.image} onChange={(e) => set("image", e.target.value)} />
                </div>
              </div>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Gallery images">
              {(p.gallery || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {(p.gallery || []).map((g, gi) => (
                    <div key={gi} className="relative group">
                      <img src={g} alt="" className="w-16 h-16 rounded-md object-cover border border-[#0e2a1e]/10" />
                      <button type="button" onClick={() => set("gallery", p.gallery.filter((_, x) => x !== gi))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-gallery-${gi}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <ImageUploader multiple onUploaded={(url) => set("gallery", [...(p.gallery || []), url])} label="Upload gallery images" />
                <input className={inputCls} placeholder="…or paste URLs, comma separated" value={(p.gallery || []).join(", ")} onChange={(e) => set("gallery", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
              </div>
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Description"><textarea rows={2} className={inputCls} value={p.description} onChange={(e) => set("description", e.target.value)} /></Field>
          </div>
          <Field label="Subcollection id"><input className={inputCls} value={p.subcollection} onChange={(e) => set("subcollection", e.target.value)} /></Field>
          <Field label="Tagline (services)"><input className={inputCls} value={p.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>

          {p.type === "service" && (
            <>
              <Field label="Location"><input className={inputCls} value={p.location} onChange={(e) => set("location", e.target.value)} /></Field>
              <Field label="Turnaround"><input className={inputCls} value={p.turnaround} onChange={(e) => set("turnaround", e.target.value)} /></Field>
              <div className="col-span-2">
                <Field label="Deliverables (one per line)">
                  <textarea rows={3} className={inputCls} value={(p.deliverables || []).join("\n")} onChange={(e) => set("deliverables", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
                </Field>
              </div>
            </>
          )}

          {/* Variants */}
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Variants</span>
              <button onClick={() => set("variants", [...(p.variants || []), { name: "", price: 0 }])} className="text-xs text-[#2f6b1e] font-medium flex items-center gap-1"><Plus size={14} /> Add variant</button>
            </div>
            {(p.variants || []).map((v, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input className={inputCls} placeholder="Variant name" value={v.name} onChange={(e) => { const nv = [...p.variants]; nv[i] = { ...nv[i], name: e.target.value }; set("variants", nv); }} />
                <input type="number" step="0.01" className="w-28 bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-3 py-2 text-sm outline-none" placeholder="Price" value={v.price} onChange={(e) => { const nv = [...p.variants]; nv[i] = { ...nv[i], price: e.target.value }; set("variants", nv); }} />
                <button onClick={() => set("variants", p.variants.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 px-2"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>

          {/* Sizes */}
          <div className="col-span-2">
            <span className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Sizes (for apparel)</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {SIZE_OPTIONS.map((s) => {
                const active = (p.sizes || []).includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => set("sizes", active ? p.sizes.filter((x) => x !== s) : [...(p.sizes || []), s])}
                    className={`min-w-[48px] px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${active ? "border-[#0e2a1e] bg-[#0e2a1e] text-[#f4f2ea]" : "border-[#0e2a1e]/20 text-[#0e2a1e] hover:border-[#0e2a1e]/50"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#0e2a1e]/50 mt-1">Selected sizes appear as options on the product page. Leave empty for items without sizes.</p>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#0e2a1e]">
            <input type="checkbox" checked={p.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured
          </label>
          <label className="flex items-center gap-2 text-sm text-[#0e2a1e]">
            <input type="checkbox" checked={p.inStock} onChange={(e) => set("inStock", e.target.checked)} /> In stock
          </label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#0e2a1e]/20 text-sm text-[#0e2a1e]">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-6 py-2.5 rounded-full bg-[#0e2a1e] text-[#f4f2ea] text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryForm = ({ initial, onSave, onClose }) => {
  const [c, setC] = useState({ name: "", image: "", subcollections: [], order: 0, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC((prev) => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">{initial?.id ? "Edit" : "New"} category</h3>
          <button onClick={onClose} className="text-[#0e2a1e]/50 hover:text-[#0e2a1e]"><X size={22} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Name"><input className={inputCls} value={c.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Image">
            <div className="flex items-center gap-3">
              {c.image ? (
                <img src={c.image} alt="" className="w-14 h-14 rounded-md object-cover bg-[#f4f2ea] border border-[#0e2a1e]/10" />
              ) : (
                <div className="w-14 h-14 rounded-md bg-[#f4f2ea] border border-dashed border-[#0e2a1e]/20 flex items-center justify-center text-[#0e2a1e]/30"><ImagePlus size={18} /></div>
              )}
              <div className="flex-1 space-y-1.5">
                <ImageUploader onUploaded={(url) => set("image", url)} label="Upload image" />
                <input className={inputCls} placeholder="…or paste an image URL" value={c.image} onChange={(e) => set("image", e.target.value)} />
              </div>
            </div>
          </Field>
          <Field label="Order"><input type="number" className={inputCls} value={c.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} /></Field>
          <Field label="Subcollections (format: id|Name, one per line)">
            <textarea rows={3} className={inputCls}
              value={(c.subcollections || []).map((s) => `${s.id}|${s.name}`).join("\n")}
              onChange={(e) => set("subcollections", e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [id, name] = l.split("|"); return { id: (id || "").trim(), name: (name || id || "").trim() }; }))} />
          </Field>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#0e2a1e]/20 text-sm text-[#0e2a1e]">Cancel</button>
          <button onClick={async () => { setSaving(true); try { await onSave(c); } finally { setSaving(false); } }} disabled={saving} className="px-6 py-2.5 rounded-full bg-[#0e2a1e] text-[#f4f2ea] text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const FULFILL_OPTIONS = ["new", "confirmed", "picked_up", "completed"];

const AdminDashboard = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [editCategory, setEditCategory] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");
  const [slotCapPickup, setSlotCapPickup] = useState(2);
  const [slotCapBooking, setSlotCapBooking] = useState(2);
  const [savingCap, setSavingCap] = useState(false);
  const [salesReport, setSalesReport] = useState([]);
  const [emailHealth, setEmailHealth] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [cfgKey, setCfgKey] = useState("");
  const [cfgSender, setCfgSender] = useState("");
  const [cfgOwner, setCfgOwner] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [calView, setCalView] = useState("week");
  const [calFilter, setCalFilter] = useState("all"); // all | pickup | booking
  const [calCursor, setCalCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });

  const loadAll = async () => {
    try {
      const [s, p, c, o] = await Promise.all([
        adminStats().catch(() => null),
        fetchProducts().catch(() => []),
        fetchCategories().catch(() => []),
        adminOrders().catch(() => []),
      ]);
      setStats(s); setProducts(p); setCategories(c); setOrders(o);
      adminMessages().then(setMessages).catch(() => {});
      adminBlockedDates().then(setBlockedDates).catch(() => {});
      adminGetSettings().then((r) => { setSlotCapPickup(r.slot_capacity_pickup); setSlotCapBooking(r.slot_capacity_booking); }).catch(() => {});
      adminSalesReport().then(setSalesReport).catch(() => {});
      adminEmailHealth().then(setEmailHealth).catch(() => {});
    } catch {}
  };

  useEffect(() => { loadAll(); }, []);

  const doLogout = async () => { await signOut(); navigate("/admin/login"); };

  const saveProduct = async (data) => {
    try {
      if (data.id) await adminUpdateProduct(data.id, data);
      else await adminCreateProduct(data);
      toast({ title: "Saved" });
      setEditProduct(null);
      loadAll();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const removeProduct = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await adminDeleteProduct(id); toast({ title: "Deleted" }); loadAll();
  };
  const saveCategory = async (data) => {
    try {
      if (data.id) await adminUpdateCategory(data.id, data);
      else await adminCreateCategory(data);
      toast({ title: "Saved" }); setEditCategory(null); loadAll();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const removeCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await adminDeleteCategory(id); toast({ title: "Deleted" }); loadAll();
  };
  const changeFulfill = async (id, status) => {
    await adminUpdateOrder(id, status); loadAll();
  };
  const removeOrder = async (id) => {
    if (!window.confirm("Delete this order/booking? This cannot be undone.")) return;
    try {
      await adminDeleteOrder(id); toast({ title: "Deleted" }); loadAll();
    } catch (e) { toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const addBlockedDate = async () => {
    if (!newBlockDate) { toast({ title: "Pick a date first", variant: "destructive" }); return; }
    try {
      await adminAddBlockedDate({ date: newBlockDate, reason: newBlockReason });
      setNewBlockDate(""); setNewBlockReason("");
      toast({ title: "Date blocked" });
      adminBlockedDates().then(setBlockedDates).catch(() => {});
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const removeBlockedDate = async (id) => {
    await adminDeleteBlockedDate(id); toast({ title: "Unblocked" });
    adminBlockedDates().then(setBlockedDates).catch(() => {});
  };
  const saveEmailConfig = async () => {
    if (!cfgKey.trim() && !cfgSender.trim() && !cfgOwner.trim()) { toast({ title: "Enter something to save", variant: "destructive" }); return; }
    setSavingCfg(true);
    try {
      const r = await adminSaveEmailConfig({ resend_api_key: cfgKey.trim() || undefined, sender_email: cfgSender.trim() || undefined, owner_email: cfgOwner.trim() || undefined });
      setEmailHealth((h) => ({ ...(h || {}), ...r }));
      setCfgKey("");
      toast({ title: "Email settings saved", description: `Sending from ${r.sender}${r.owner_email ? ` · alerts to ${r.owner_email}` : ""}` });
      adminEmailHealth().then(setEmailHealth).catch(() => {});
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSavingCfg(false); }
  };

  const sendTest = async () => {    if (!testTo || !testTo.includes("@")) { toast({ title: "Enter a valid email", variant: "destructive" }); return; }
    setSendingTest(true); setTestResult(null);
    try {
      const r = await adminSendTestEmail(testTo.trim());
      setTestResult(r);
      toast({ title: r.ok ? "Test email sent" : "Test email failed", variant: r.ok ? undefined : "destructive" });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Request failed" });
    } finally { setSendingTest(false); }
  };

  const saveCapacity = async () => {    setSavingCap(true);
    try {
      const r = await adminUpdateSettings({
        slot_capacity_pickup: parseInt(slotCapPickup, 10) || 1,
        slot_capacity_booking: parseInt(slotCapBooking, 10) || 1,
      });
      setSlotCapPickup(r.slot_capacity_pickup);
      setSlotCapBooking(r.slot_capacity_booking);
      toast({ title: `Limits saved — pick-ups: ${r.slot_capacity_pickup}, sessions: ${r.slot_capacity_booking}` });
    } catch (e) { toast({ title: "Failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSavingCap(false); }
  };

  const buildReportRows = () => {
    const headers = ["Month", "Orders", "Subtotal", "HST collected", "Donations", "Revenue (CAD)"];
    const rows = salesReport.map((r) => [
      new Date(r.month + "-01T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" }),
      r.orders, r.subtotal.toFixed(2), r.tax.toFixed(2), r.donations.toFixed(2), r.revenue.toFixed(2),
    ]);
    const totals = ["All-time",
      salesReport.reduce((s, r) => s + r.orders, 0),
      salesReport.reduce((s, r) => s + r.subtotal, 0).toFixed(2),
      salesReport.reduce((s, r) => s + r.tax, 0).toFixed(2),
      salesReport.reduce((s, r) => s + r.donations, 0).toFixed(2),
      salesReport.reduce((s, r) => s + r.revenue, 0).toFixed(2),
    ];
    return { headers, rows: [...rows, totals] };
  };
  const exportReportCsv = () => {
    if (salesReport.length === 0) { toast({ title: "No sales to export", variant: "destructive" }); return; }
    const { headers, rows } = buildReportRows();
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sales-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportReportXlsx = () => {
    if (salesReport.length === 0) { toast({ title: "No sales to export", variant: "destructive" }); return; }
    const { headers, rows } = buildReportRows();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales report");
    XLSX.writeFile(wb, `sales-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const buildPaidRows = () => {
    const paid = orders.filter((o) => o.payment_status === "paid");
    const headers = ["Order ID", "Date", "Type", "Name", "Email", "Phone", "Items", "Subtotal", "HST", "Donation", "Total (CAD)", "Fulfillment", "Pick-up/Session Date", "Time"];
    const rows = paid.map((o) => [
      o.id,
      new Date(o.created_at).toLocaleString(),
      o.kind || "order",
      o.customer?.name || "",
      o.customer?.email || "",
      o.customer?.phone || "",
      (o.items || []).map((it) => `${it.name}${it.variant ? ` (${it.variant})` : ""} x${it.qty}`).join("; "),
      (o.subtotal ?? 0).toFixed(2),
      (o.tax ?? 0).toFixed(2),
      (o.donation ?? 0).toFixed(2),
      (o.amount ?? 0).toFixed(2),
      o.fulfillment_status || "",
      o.schedule?.date || "",
      o.schedule?.time || "",
    ]);
    return { paid, headers, rows };
  };
  const exportPaidOrdersCsv = () => {
    const { paid, headers, rows } = buildPaidRows();
    if (paid.length === 0) { toast({ title: "No paid orders to export", variant: "destructive" }); return; }
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paid-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportPaidOrdersXlsx = () => {
    const { paid, headers, rows } = buildPaidRows();
    if (paid.length === 0) { toast({ title: "No paid orders to export", variant: "destructive" }); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Paid orders");
    XLSX.writeFile(wb, `paid-orders-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ---- Calendar helpers ----
  const timeToMinutes = (t) => {
    const m = String(t || "").match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10) % 12;
    if (/pm/i.test(m[3])) h += 12;
    return h * 60 + parseInt(m[2], 10);
  };
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const addDays = (d, n) => { const x = new Date(d); x.setDate(d.getDate() + n); return x; };
  const eventsByDay = () => {
    const map = {};
    orders.forEach((o) => {
      if (!o.schedule?.date || o.kind === "donation" || o.payment_status !== "paid") return;
      if (calFilter === "booking" && o.kind !== "booking") return;
      if (calFilter === "pickup" && o.kind === "booking") return;
      const d = new Date(o.schedule.date);
      if (isNaN(d.getTime())) return;
      const key = ymd(d);
      (map[key] = map[key] || []).push({ o, min: timeToMinutes(o.schedule.time) });
    });
    Object.values(map).forEach((a) => a.sort((x, y) => x.min - y.min));
    return map;
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: DollarSign },    { id: "products", label: "Products & Services", icon: Package },
    { id: "categories", label: "Categories", icon: Grid3x3 },
    { id: "orders", label: "Orders & Bookings", icon: ShoppingCart },
    { id: "calendar", label: "Calendar", icon: CalendarDays },
    { id: "availability", label: "Availability", icon: CalendarOff },
    { id: "reports", label: "Sales Report", icon: BarChart3 },
    { id: "messages", label: "Messages", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-[#f4f2ea]">
      {/* Top bar */}
      <div className="bg-[#0e2a1e] text-[#f4f2ea]">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Everyone's Budget" className="w-9 h-9 rounded-full object-cover" />
            <span className="font-serif-brand text-xl font-semibold">Everyone's Budget · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#f4f2ea]/70 hidden sm:block">{user?.email}</span>
            <button onClick={doLogout} className="flex items-center gap-2 text-sm bg-[#f4f2ea]/10 hover:bg-[#f4f2ea]/20 px-3 py-1.5 rounded-full"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.id ? "bg-[#0e2a1e] text-[#f4f2ea]" : "bg-white text-[#0e2a1e] border border-[#0e2a1e]/10 hover:border-[#0e2a1e]/30"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { label: "Revenue (paid)", value: `$${stats.revenue?.toFixed(2)}`, icon: DollarSign },
              { label: "Paid orders", value: stats.paid_orders, icon: CheckCircle2 },
              { label: "Pending", value: stats.pending_orders, icon: Clock },
              { label: "Products", value: stats.products, icon: Package },
              { label: "Services", value: stats.services, icon: Camera },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-2xl p-5 border border-[#0e2a1e]/8">
                <c.icon size={20} className="text-[#2f6b1e]" />
                <p className="text-2xl font-semibold text-[#0e2a1e] mt-3">{c.value}</p>
                <p className="text-xs text-[#0e2a1e]/60 mt-1">{c.label}</p>
              </div>
            ))}
            <div className={`col-span-2 md:col-span-3 lg:col-span-5 rounded-2xl p-4 text-sm flex items-center gap-3 ${stats.email_configured ? "bg-[#a4d61e]/15 text-[#2f6b1e]" : "bg-amber-100 text-amber-800"}`}>
              <Mail size={18} />
              {stats.email_configured ? "Gmail confirmation emails are ON." : "Gmail emails are OFF — add GMAIL_ADDRESS & GMAIL_APP_PASSWORD in backend .env to enable."}
            </div>
          </div>
        )}

        {/* Products */}
        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">Products & Services</h2>
              <button onClick={() => setEditProduct({})} className="flex items-center gap-2 bg-[#a4d61e] text-[#0e2a1e] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#b6e63a]"><Plus size={16} /> Add item</button>
            </div>
            <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 overflow-hidden">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 border-b border-[#0e2a1e]/5 last:border-0">
                  <img src={p.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#f4f2ea]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0e2a1e] truncate">{p.name}</p>
                    <p className="text-xs text-[#0e2a1e]/60">{p.type === "service" ? "Service" : "Product"} · {p.category} · ${p.price?.toFixed(2)} {p.featured && "· ★ Featured"}</p>
                    {p.type === "product" && (
                      <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${p.stock <= 0 ? "bg-red-100 text-red-700" : p.stock <= 5 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                        {p.stock <= 0 ? "Out of stock" : `${p.stock} in stock`}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setEditProduct(p)} className="p-2 text-[#0e2a1e]/60 hover:text-[#0e2a1e]"><Pencil size={16} /></button>
                  <button onClick={() => removeProduct(p.id)} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        {tab === "categories" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">Categories</h2>
              <button onClick={() => setEditCategory({})} className="flex items-center gap-2 bg-[#a4d61e] text-[#0e2a1e] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#b6e63a]"><Plus size={16} /> Add category</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-4 flex gap-4">
                  <img src={c.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="font-medium text-[#0e2a1e]">{c.name}</p>
                    <p className="text-xs text-[#0e2a1e]/60 mt-1">{(c.subcollections || []).map((s) => s.name).join(", ") || "No subcollections"}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditCategory(c)} className="p-2 text-[#0e2a1e]/60 hover:text-[#0e2a1e]"><Pencil size={16} /></button>
                    <button onClick={() => removeCategory(c.id)} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders */}
        {tab === "orders" && (
          <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">Orders & Bookings</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={exportPaidOrdersCsv}
                  data-testid="export-orders-csv-btn"
                  className="flex items-center gap-2 bg-[#0e2a1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#153a2a] transition-colors"
                >
                  <Download size={16} /> CSV
                </button>
                <button
                  onClick={exportPaidOrdersXlsx}
                  data-testid="export-orders-xlsx-btn"
                  className="flex items-center gap-2 bg-[#2f6b1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#3a8226] transition-colors"
                >
                  <Download size={16} /> Excel (.xlsx)
                </button>
              </div>
            </div>
            {orders.length === 0 && <p className="text-[#0e2a1e]/60">No orders yet.</p>}
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${o.kind === "booking" ? "bg-[#a4d61e]/25 text-[#2f6b1e]" : o.kind === "donation" ? "bg-pink-100 text-pink-700" : "bg-[#0e2a1e]/10 text-[#0e2a1e]"}`}>
                          {o.kind === "booking" ? "Booking" : o.kind === "donation" ? "Donation" : "Order"}
                        </span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span>
                      </div>
                      <p className="font-medium text-[#0e2a1e] mt-2">{o.customer?.name} · <span className="text-[#0e2a1e]/60 font-normal">{o.customer?.email}</span></p>
                      {o.customer?.phone && <p className="text-xs text-[#0e2a1e]/60">{o.customer.phone}</p>}
                      {o.customer?.note && <p className="text-xs text-[#0e2a1e]/60 mt-1 italic">"{o.customer.note}"</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#0e2a1e]">${o.amount?.toFixed(2)} CAD</p>
                      <p className="text-[11px] text-[#0e2a1e]/50">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="mt-3 border-t border-[#0e2a1e]/5 pt-3 text-sm text-[#0e2a1e]/75">
                    {o.items?.map((it, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{it.name}{it.variant ? ` — ${it.variant}` : ""} × {it.qty}</span>
                        <span>${(it.price * it.qty).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-[#0e2a1e]/60">Status:</span>
                    <select value={o.fulfillment_status} onChange={(e) => changeFulfill(o.id, e.target.value)} className="text-sm bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-2 py-1 outline-none">
                      {FULFILL_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                    {o.schedule?.date && (
                      <span className="text-xs text-[#2f6b1e] font-medium flex items-center gap-1">
                        <Clock size={13} /> {o.schedule.date} · {o.schedule.time}
                      </span>
                    )}
                    <button onClick={() => removeOrder(o.id)} className="ml-auto flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-md px-3 py-1 transition-colors">
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calendar */}
        {tab === "calendar" && (() => {
          const evMap = eventsByDay();
          const isToday = (d) => ymd(d) === ymd(new Date());
          const weekStart = (() => { const s = new Date(calCursor); s.setDate(calCursor.getDate() - calCursor.getDay()); s.setHours(0, 0, 0, 0); return s; })();
          const days = calView === "week" ? Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)) : [calCursor];
          const shift = (n) => setCalCursor((c) => addDays(c, n));
          const EventCard = ({ ev }) => {
            const o = ev.o;
            const isBooking = o.kind === "booking";
            const itemNames = (o.items || []).filter((it) => it.type !== "donation").map((it) => it.name).join(", ");
            return (
              <div data-testid={`cal-event-${o.id}`} className={`rounded-lg p-2.5 text-left border ${isBooking ? "bg-[#a4d61e]/15 border-[#a4d61e]/40" : "bg-white border-[#0e2a1e]/10"}`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0e2a1e]">
                  <Clock size={12} className="text-[#2f6b1e]" /> {o.schedule?.time}
                  <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isBooking ? "bg-[#2f6b1e] text-[#f4f2ea]" : "bg-[#0e2a1e]/10 text-[#0e2a1e]"}`}>{isBooking ? "Session" : "Pick-up"}</span>
                </div>
                <p className="text-xs font-medium text-[#0e2a1e] mt-1 truncate">{o.customer?.name || "—"}</p>
                {itemNames && <p className="text-[11px] text-[#0e2a1e]/55 truncate">{itemNames}</p>}
                {o.customer?.phone && <p className="text-[11px] text-[#0e2a1e]/45">{o.customer.phone}</p>}
              </div>
            );
          };
          return (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">Calendar</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex bg-white rounded-full border border-[#0e2a1e]/10 p-0.5">
                    {[["all", "Both"], ["pickup", "Pick-ups"], ["booking", "Sessions"]].map(([v, label]) => (
                      <button key={v} data-testid={`cal-filter-${v}`} onClick={() => setCalFilter(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${calFilter === v ? "bg-[#2f6b1e] text-[#f4f2ea]" : "text-[#0e2a1e]"}`}>{label}</button>
                    ))}
                  </div>
                  <div className="flex bg-white rounded-full border border-[#0e2a1e]/10 p-0.5">
                    {["week", "day"].map((v) => (
                      <button key={v} data-testid={`cal-view-${v}`} onClick={() => setCalView(v)} className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${calView === v ? "bg-[#0e2a1e] text-[#f4f2ea]" : "text-[#0e2a1e]"}`}>{v}</button>
                    ))}
                  </div>
                  <button data-testid="cal-prev" onClick={() => shift(calView === "week" ? -7 : -1)} className="p-2 bg-white rounded-full border border-[#0e2a1e]/10 hover:border-[#0e2a1e]/30"><ChevronLeft size={16} /></button>
                  <button data-testid="cal-today" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setCalCursor(d); }} className="px-3 py-2 bg-white rounded-full border border-[#0e2a1e]/10 text-sm font-medium hover:border-[#0e2a1e]/30">Today</button>
                  <button data-testid="cal-next" onClick={() => shift(calView === "week" ? 7 : 1)} className="p-2 bg-white rounded-full border border-[#0e2a1e]/10 hover:border-[#0e2a1e]/30"><ChevronRight size={16} /></button>
                </div>
              </div>
              <p className="text-sm text-[#0e2a1e]/60 mb-4">
                {calView === "week"
                  ? `Week of ${weekStart.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}`
                  : calCursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </p>
              <div className={calView === "week" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" : "max-w-xl"}>
                {days.map((d) => {
                  const evs = evMap[ymd(d)] || [];
                  return (
                    <div key={ymd(d)} className={`bg-[#f4f2ea] rounded-xl border ${isToday(d) ? "border-[#2f6b1e]" : "border-[#0e2a1e]/8"} p-2 min-h-[120px]`}>
                      <div className={`text-xs font-semibold mb-2 px-1 ${isToday(d) ? "text-[#2f6b1e]" : "text-[#0e2a1e]/70"}`}>
                        {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
                        {isToday(d) && <span className="ml-1 text-[10px]">• Today</span>}
                      </div>
                      <div className="space-y-2">
                        {evs.length === 0 ? <p className="text-[11px] text-[#0e2a1e]/35 px-1">—</p> : evs.map((ev, i) => <EventCard key={i} ev={ev} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Calendar end */}
        {/* Availability (blocked dates) */}
        {tab === "availability" && (
          <div>
            <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Mail size={18} className="text-[#2f6b1e]" />
                <h2 className="font-serif-brand text-xl font-semibold text-[#0e2a1e]">Email diagnostics</h2>
              </div>
              {emailHealth && (
                <div className="text-sm text-[#0e2a1e]/80 space-y-1 mb-3">
                  <p>Provider: <strong>{emailHealth.provider}</strong> · Emails enabled: <strong>{emailHealth.email_configured ? "Yes" : "No"}</strong></p>
                  <p>Sending from: <strong data-testid="email-sender">{emailHealth.from_name} &lt;{emailHealth.sender}&gt;</strong></p>
                  <p>Resend key: <strong>{emailHealth.resend_key_present ? `set (…${emailHealth.resend_key_tail})` : "not set"}</strong></p>
                  <p>Owner notifications to: <strong>{emailHealth.owner_email || "—"}</strong></p>
                </div>
              )}

              <div className="bg-[#f4f2ea] rounded-xl p-4 mb-4 border border-[#0e2a1e]/8">
                <p className="text-xs font-semibold text-[#0e2a1e] mb-2">Update email credentials (applies to this live site instantly)</p>
                <div className="space-y-2">
                  <input type="text" data-testid="cfg-resend-key-input" placeholder="Resend API key (re_...)" value={cfgKey} onChange={(e) => setCfgKey(e.target.value)} className={inputCls} />
                  <input type="email" data-testid="cfg-sender-input" placeholder="Sender email (e.g. info@everyonesbudget.ca)" value={cfgSender} onChange={(e) => setCfgSender(e.target.value)} className={inputCls} />
                  <input type="email" data-testid="cfg-owner-input" placeholder="Owner email for order alerts (e.g. info@everyonesbudget.ca)" value={cfgOwner} onChange={(e) => setCfgOwner(e.target.value)} className={inputCls} />
                  <button onClick={saveEmailConfig} disabled={savingCfg} data-testid="save-email-config-btn" className="flex items-center gap-2 bg-[#2f6b1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#3a8226] disabled:opacity-60">
                    {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save email settings
                  </button>
                </div>
                <p className="text-[11px] text-[#0e2a1e]/50 mt-2">Paste your Resend key here to fix sending on the live site without redeploying. Leave sender blank to keep the current one.</p>
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Send a test email to</label>
                  <input type="email" data-testid="test-email-input" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className={`${inputCls} mt-1`} />
                </div>
                <button onClick={sendTest} disabled={sendingTest} data-testid="send-test-email-btn" className="flex items-center gap-2 bg-[#0e2a1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#153a2a] disabled:opacity-60">
                  {sendingTest ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Send test
                </button>
              </div>
              {testResult && (
                <div data-testid="test-email-result" className={`mt-3 rounded-lg p-3 text-sm ${testResult.ok ? "bg-[#a4d61e]/15 text-[#0e2a1e]" : "bg-red-50 text-red-700 border border-red-200"}`}>
                  {testResult.ok
                    ? `✓ Sent to ${testResult.to} from ${testResult.sender}. Check that inbox (and spam).`
                    : `✕ Failed: ${testResult.error}`}
                </div>
              )}
              <p className="text-[11px] text-[#0e2a1e]/50 mt-3">Tip: also check your delivery logs at resend.com/emails to see every send and its status.</p>
            </div>

            <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] mb-2">Booking limits</h2>
            <p className="text-sm text-[#0e2a1e]/60 mb-5">Control how many pick-ups and sessions can be booked per time slot.</p>

            <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5 mb-6">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Max pick-ups per slot</label>
                  <input type="number" min="1" step="1" data-testid="slot-capacity-pickup-input" value={slotCapPickup} onChange={(e) => setSlotCapPickup(e.target.value)} className={`${inputCls} mt-1 w-40`} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Max photography sessions per slot</label>
                  <input type="number" min="1" step="1" data-testid="slot-capacity-booking-input" value={slotCapBooking} onChange={(e) => setSlotCapBooking(e.target.value)} className={`${inputCls} mt-1 w-40`} />
                </div>
                <button onClick={saveCapacity} disabled={savingCap} data-testid="save-slot-capacity-btn" className="flex items-center gap-2 bg-[#0e2a1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#153a2a] disabled:opacity-60">
                  {savingCap ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save limits
                </button>
              </div>
              <p className="text-[11px] text-[#0e2a1e]/50 mt-2">Once a time slot reaches its limit it shows as "Full" and customers must pick another time. Pick-ups and photography sessions are counted separately.</p>
            </div>

            <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] mb-2">Blocked Dates</h2>
            <p className="text-sm text-[#0e2a1e]/60 mb-5">Block holidays or days you're away.</p>

            <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5 mb-6">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Date</label>
                  <input type="date" data-testid="block-date-input" value={newBlockDate} onChange={(e) => setNewBlockDate(e.target.value)} className={`${inputCls} mt-1`} />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Reason (optional)</label>
                  <input data-testid="block-reason-input" placeholder="e.g. Christmas Day" value={newBlockReason} onChange={(e) => setNewBlockReason(e.target.value)} className={`${inputCls} mt-1`} />
                </div>
                <button onClick={addBlockedDate} data-testid="add-blocked-date-btn" className="flex items-center gap-2 bg-[#a4d61e] text-[#0e2a1e] font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#b6e63a]">
                  <Plus size={16} /> Block date
                </button>
              </div>
            </div>

            {blockedDates.length === 0 ? (
              <p className="text-[#0e2a1e]/60">No blocked dates. All available days are open for scheduling.</p>
            ) : (
              <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 overflow-hidden">
                {blockedDates.map((b) => (
                  <div key={b.id} className="flex items-center gap-4 p-4 border-b border-[#0e2a1e]/5 last:border-0">
                    <CalendarOff size={18} className="text-red-500" />
                    <div className="flex-1">
                      <p className="font-medium text-[#0e2a1e]">{new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
                      {b.reason && <p className="text-xs text-[#0e2a1e]/60">{b.reason}</p>}
                    </div>
                    <button onClick={() => removeBlockedDate(b.id)} data-testid={`remove-blocked-date-${b.id}`} className="p-2 text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sales Report */}
        {tab === "reports" && (
          <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
              <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">Monthly Sales Report</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={exportReportCsv} data-testid="export-report-csv-btn" className="flex items-center gap-2 bg-[#0e2a1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#153a2a] transition-colors">
                  <Download size={16} /> CSV
                </button>
                <button onClick={exportReportXlsx} data-testid="export-report-xlsx-btn" className="flex items-center gap-2 bg-[#2f6b1e] text-[#f4f2ea] font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#3a8226] transition-colors">
                  <Download size={16} /> Excel (.xlsx)
                </button>
              </div>
            </div>
            <p className="text-sm text-[#0e2a1e]/60 mb-5">Totals for paid orders each month — handy for bookkeeping and HST remittance.</p>
            {salesReport.length === 0 ? (
              <p className="text-[#0e2a1e]/60">No paid sales yet.</p>
            ) : (
              <div className="bg-white rounded-2xl border border-[#0e2a1e]/8 overflow-x-auto">
                <table className="w-full text-sm" data-testid="sales-report-table">
                  <thead>
                    <tr className="text-left text-[#0e2a1e]/60 border-b border-[#0e2a1e]/8">
                      <th className="p-4 font-semibold">Month</th>
                      <th className="p-4 font-semibold text-right">Orders</th>
                      <th className="p-4 font-semibold text-right">Subtotal</th>
                      <th className="p-4 font-semibold text-right">HST collected</th>
                      <th className="p-4 font-semibold text-right">Donations</th>
                      <th className="p-4 font-semibold text-right">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesReport.map((r) => (
                      <tr key={r.month} className="border-b border-[#0e2a1e]/5 last:border-0 text-[#0e2a1e]">
                        <td className="p-4 font-medium">{new Date(r.month + "-01T00:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" })}</td>
                        <td className="p-4 text-right">{r.orders}</td>
                        <td className="p-4 text-right">${r.subtotal.toFixed(2)}</td>
                        <td className="p-4 text-right text-[#2f6b1e] font-medium">${r.tax.toFixed(2)}</td>
                        <td className="p-4 text-right">${r.donations.toFixed(2)}</td>
                        <td className="p-4 text-right font-semibold">${r.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#f4f2ea] text-[#0e2a1e] font-semibold">
                      <td className="p-4">All-time</td>
                      <td className="p-4 text-right">{salesReport.reduce((s, r) => s + r.orders, 0)}</td>
                      <td className="p-4 text-right">${salesReport.reduce((s, r) => s + r.subtotal, 0).toFixed(2)}</td>
                      <td className="p-4 text-right text-[#2f6b1e]">${salesReport.reduce((s, r) => s + r.tax, 0).toFixed(2)}</td>
                      <td className="p-4 text-right">${salesReport.reduce((s, r) => s + r.donations, 0).toFixed(2)}</td>
                      <td className="p-4 text-right">${salesReport.reduce((s, r) => s + r.revenue, 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {tab === "messages" && (
          <div>
            <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] mb-4">Contact Messages</h2>
            {messages.length === 0 && <p className="text-[#0e2a1e]/60">No messages yet.</p>}
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl border border-[#0e2a1e]/8 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-[#0e2a1e]">{m.name} · <a href={`mailto:${m.email}`} className="text-[#a4820e] hover:underline font-normal">{m.email}</a></p>
                    <span className="text-[11px] text-[#0e2a1e]/50">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-[#0e2a1e]/75 mt-3 whitespace-pre-wrap">{m.message}</p>
                  <a href={`mailto:${m.email}`} className="inline-block mt-3 text-sm text-[#2f6b1e] font-medium hover:underline">Reply →</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editProduct && <ProductForm initial={editProduct} categories={categories} onSave={saveProduct} onClose={() => setEditProduct(null)} />}
      {editCategory && <CategoryForm initial={editCategory} onSave={saveCategory} onClose={() => setEditCategory(null)} />}
    </div>
  );
};

export default AdminDashboard;
